import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { trackEvent } from '../../services/analytics';
import {
  useDiceSubscriptions,
  useDiceCancelSubscription,
  useDiceCancelPending,
  useDiceCreateCheckout,
  useDiceResubscribe,
  useDiceUpdateEmail,
  useDiceBillingPortal,
  useDiceMySMS,
  useDiceRequestOTP,
  useDiceBulkCreate,
  useDiceBulkJob,
  useDiceBulkJobs,
  useDiceBulkCancel,
  queryKeys,
} from '../../hooks/useApi';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Input } from '../../components/base-components';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { DiceSubscription } from '../../types';
import { PageShell } from '../components/PageShell';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { Badge, BadgeTone } from '../components/Badge';
import { DataList, DataListColumn, DataListRow } from '../components/DataList';
import { SegmentedControl, SegmentOption } from '../components/SegmentedControl';
import { ChevronDownIcon, CloseIcon, SearchIcon } from '../components/icons';
import { EmptyState } from '../components/EmptyState';
import { SkeletonCard } from '../components/Skeleton';
import { cx, CARD_SURFACE, FOCUS_RING } from '../components/styles';
import { v2Path } from '../nav';

/** `<Link>` styled as a `Button` — a real link, so it survives middle-click. */
const linkButtonClass = (variant: 'primary' | 'secondary' = 'primary') =>
  cx(
    'inline-flex items-center justify-center rounded-lg px-4 py-2 text-xs font-medium transition-all',
    variant === 'primary'
      ? 'bg-[#3b82f6] text-white shadow-lg shadow-[#3b82f6]/30 hover:-translate-y-0.5 hover:bg-[#2563eb] hover:shadow-xl hover:shadow-[#3b82f6]/40'
      : 'border border-white/10 bg-white/5 text-white hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/10',
    FOCUS_RING,
  );

function StatusBadge({ status, cancelAtPeriodEnd }: { status: string; cancelAtPeriodEnd?: boolean }) {
  let tone: BadgeTone = 'neutral';
  let label = status;

  if (status === 'active' && !cancelAtPeriodEnd) {
    tone = 'green';
    label = 'Active';
  } else if (status === 'active' && cancelAtPeriodEnd) {
    tone = 'amber';
    label = 'Cancels at period end';
  } else if (status === 'past_due') {
    tone = 'amber';
    label = 'Past Due';
  } else if (status === 'canceled') {
    tone = 'red';
    label = 'Canceled';
  } else if (status === 'pending') {
    tone = 'blue';
    label = 'Pending';
  }

  return <Badge tone={tone}>{label}</Badge>;
}

function PhoneStatusBadge({ status }: { status: string | null }) {
  if (!status) return null;
  const map: Record<string, { tone: BadgeTone; label: string }> = {
    IN_USE: { tone: 'green', label: 'Active' },
    ASSIGNED: { tone: 'amber', label: 'Setting up' },
    AVAILABLE: { tone: 'neutral', label: 'Available' },
  };
  const entry = map[status] || { tone: 'neutral' as BadgeTone, label: status };
  return <Badge tone={entry.tone}>{entry.label}</Badge>;
}

function ForwardingStatusBadge({ status }: { status: string }) {
  const map: Record<string, { tone: BadgeTone; label: string }> = {
    FORWARDED: { tone: 'green', label: 'Forwarded' },
    PENDING: { tone: 'amber', label: 'Pending' },
    FAILED: { tone: 'red', label: 'Failed' },
    NO_EMAIL: { tone: 'neutral', label: 'No email set' },
  };
  const entry = map[status] || { tone: 'neutral' as BadgeTone, label: status };
  return <Badge tone={entry.tone}>{entry.label}</Badge>;
}

/** One label/value pair in the subscription detail grid. */
const Detail: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="min-w-0">
    <p className="text-xs text-white/40">{label}</p>
    <p className="mt-0.5 break-words text-sm text-white/70">{children}</p>
  </div>
);

const retryInputClass =
  'w-full bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white placeholder-white/30 focus:outline-none focus:border-[#3b82f6]/50';

/* ------------------------------------------------------------------ *
 * Triage model
 *
 * 37 subscriptions is the real production shape, so the list is ordered
 * by "what needs a human" rather than by insertion order. Every condition
 * below is one the v1 card already rendered somewhere — this only decides
 * where a row sorts, it never invents a state.
 * ------------------------------------------------------------------ */

type Bucket = 'attention' | 'ending' | 'active' | 'inactive';

interface Flags {
  needsSetup: boolean;
  needsReconnect: boolean;
  noEmail: boolean;
  bucket: Bucket;
  /** Lower sorts first inside a bucket. */
  severity: number;
}

function classify(sub: DiceSubscription): Flags {
  const needsSetup = sub.is_active && !sub.connection_id;
  const needsReconnect = Boolean(sub.connection_id) && sub.connection_is_active === false;
  const noEmail = !sub.forwarding_email;
  const pastDue = sub.status === 'past_due';
  const pending = sub.status === 'pending';

  let bucket: Bucket = 'active';
  let severity = 50;

  if (pastDue) {
    bucket = 'attention';
    severity = 0;
  } else if (needsReconnect) {
    bucket = 'attention';
    severity = 1;
  } else if (needsSetup) {
    bucket = 'attention';
    severity = 2;
  } else if (pending) {
    bucket = 'attention';
    severity = 3;
  } else if (noEmail && sub.status !== 'canceled') {
    bucket = 'attention';
    severity = 4;
  } else if (sub.status === 'canceled') {
    bucket = 'inactive';
    severity = 90;
  } else if (sub.cancel_at_period_end) {
    bucket = 'ending';
    severity = 20;
  }

  return { needsSetup, needsReconnect, noEmail, bucket, severity };
}

const BUCKET_ORDER: Bucket[] = ['attention', 'ending', 'active', 'inactive'];

/** Group headings reuse the exact badge strings wherever one exists. */
const BUCKET_LABEL: Record<Bucket, string> = {
  attention: 'Needs attention',
  ending: 'Cancels at period end',
  active: 'Active',
  inactive: 'Canceled',
};

type SortMode = 'status' | 'phone' | 'newest';
type StatusFilter = 'all' | 'attention' | 'active' | 'canceled';

const SORT_MODES: SortMode[] = ['status', 'phone', 'newest'];
const STATUS_FILTERS: StatusFilter[] = ['all', 'attention', 'active', 'canceled'];

/**
 * The attention bucket used to answer to three different names: `Attention` on
 * the filter chip, `Problems first` on the sort, and `Needs attention` on the
 * group heading — for one condition. BUCKET_LABEL is the source of truth and
 * everything else quotes it, so a rename lands everywhere at once.
 */
const SORT_LABEL: Record<SortMode, string> = {
  status: `${BUCKET_LABEL.attention} first`,
  phone: 'Number',
  newest: 'Newest',
};

const VIEW_PREF_KEY = 'flashtools_v2_dice_view';

interface ViewPref {
  sort: SortMode;
  filter: StatusFilter;
}

function readViewPref(): ViewPref {
  const fallback: ViewPref = { sort: 'status', filter: 'all' };
  try {
    const raw = window.localStorage.getItem(VIEW_PREF_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<ViewPref>;
    return {
      sort: SORT_MODES.includes(parsed.sort as SortMode) ? (parsed.sort as SortMode) : fallback.sort,
      filter: STATUS_FILTERS.includes(parsed.filter as StatusFilter)
        ? (parsed.filter as StatusFilter)
        : fallback.filter,
    };
  } catch {
    // Private mode / blocked storage — the defaults are always correct.
    return fallback;
  }
}

function digitsOnly(value: string): string {
  return value.replace(/\D+/g, '');
}

/** Alphabetical by number, with the not-yet-assigned ones last. */
function byPhoneNumber(a: DiceSubscription, b: DiceSubscription): number {
  if (!a.phone_number && !b.phone_number) return 0;
  if (!a.phone_number) return 1;
  if (!b.phone_number) return -1;
  return a.phone_number.localeCompare(b.phone_number);
}

function matchesQuery(sub: DiceSubscription, query: string, queryDigits: string): boolean {
  if (!query) return true;
  const haystack = [
    sub.phone_number,
    sub.forwarding_email,
    sub.dice_email,
    sub.connection_email,
    sub.connection_phone,
    sub.dice_first_name,
    sub.dice_last_name,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (haystack.includes(query)) return true;
  // "4155551234" should find "+1 (415) 555-1234".
  if (queryDigits.length >= 3) {
    const numbers = digitsOnly([sub.phone_number, sub.connection_phone].filter(Boolean).join(' '));
    if (numbers.includes(queryDigits)) return true;
  }
  return false;
}

/* ------------------------------------------------------------------ */

const SUB_COLUMNS: DataListColumn[] = [
  { key: 'phone', label: 'Number', span: 3 },
  { key: 'email', label: 'Dice account email', span: 4 },
  { key: 'status', label: 'Status', span: 3 },
  { key: 'renewal', label: 'Renewal', span: 2 },
];

interface SubscriptionRowProps {
  sub: DiceSubscription;
  expanded: boolean;
  onToggle: () => void;
}

/**
 * One subscription. The collapsed row carries the four identifying facts;
 * every action the v1 card had lives in the always-tappable expansion below
 * it — nothing is behind a hover.
 */
function SubscriptionRow({ sub, expanded, onToggle }: SubscriptionRowProps) {
  const { showSuccess, showError } = useToast();
  const cancelMutation = useDiceCancelSubscription();
  const cancelPendingMutation = useDiceCancelPending();
  const resumeCheckoutMutation = useDiceCreateCheckout();
  const resubMutation = useDiceResubscribe();
  const updateEmailMutation = useDiceUpdateEmail();
  const retryMutation = useDiceRequestOTP();

  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [retryDiceEmail, setRetryDiceEmail] = useState('');
  const [retryFirstName, setRetryFirstName] = useState('');
  const [retryLastName, setRetryLastName] = useState('');
  const [retryDob, setRetryDob] = useState('');
  const [showRetryEmail, setShowRetryEmail] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const needsSetup = sub.is_active && !sub.connection_id;

  const today = new Date();
  const minDob = new Date(today.getFullYear() - 100, today.getMonth(), today.getDate())
    .toISOString()
    .slice(0, 10);
  const maxDob = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate())
    .toISOString()
    .slice(0, 10);

  const handleCancel = async () => {
    try {
      const data = await cancelMutation.mutateAsync({ subscriptionId: sub.id, immediate: false });
      showSuccess(data.message);
      trackEvent('dice_subscription_canceled', { subscriptionId: String(sub.id), immediate: false });
      setConfirmCancel(false);
    } catch (error: any) {
      showError(error?.message || 'Failed to cancel subscription');
    }
  };

  const handleCancelPending = async () => {
    try {
      const data = await cancelPendingMutation.mutateAsync(sub.id);
      showSuccess(data.message);
      trackEvent('dice_pending_canceled', { subscriptionId: String(sub.id) });
    } catch (error: any) {
      showError(error?.message || 'Failed to remove pending session');
    }
  };

  const handleResumePending = async () => {
    try {
      const data = await resumeCheckoutMutation.mutateAsync({
        forwardingEmail: sub.forwarding_email,
        diceEmail: sub.dice_email,
        firstName: sub.dice_first_name || undefined,
        lastName: sub.dice_last_name || undefined,
      });
      window.location.href = data.checkout_url;
    } catch (error: any) {
      showError(error?.message || 'Failed to resume checkout');
    }
  };

  const handleResubscribe = async () => {
    try {
      const data = await resubMutation.mutateAsync(sub.id);
      if (data.requires_new_checkout) {
        window.location.href = v2Path('/dice/signup');
        return;
      }
      showSuccess(data.message || 'Subscription reactivated');
      trackEvent('dice_subscription_reactivated', { subscriptionId: String(sub.id) });
    } catch (error: any) {
      showError(error?.message || 'Failed to resubscribe');
    }
  };

  const handleUpdateEmail = async () => {
    try {
      const data = await updateEmailMutation.mutateAsync({ subscriptionId: sub.id, forwardingEmail: newEmail });
      showSuccess(data.message || 'Forwarding email updated');
      trackEvent('dice_email_updated', { subscriptionId: String(sub.id) });
      setEditingEmail(false);
    } catch (error: any) {
      showError(error?.message || 'Failed to update email');
    }
  };

  const handleRetrySetup = async () => {
    try {
      const data = await retryMutation.mutateAsync({
        subscriptionId: sub.id,
        forwardingEmail: sub.forwarding_email,
        diceEmail: retryDiceEmail.trim() || undefined,
        firstName: retryFirstName.trim() || undefined,
        lastName: retryLastName.trim() || undefined,
        dob: retryDob || undefined,
      });
      showSuccess(`Setup retried — ${data.phone_number}. Account will be created automatically.`);
      trackEvent('dice_setup_retried', { subscriptionId: String(sub.id) });
      setShowRetryEmail(false);
      setRetryDiceEmail('');
      setRetryFirstName('');
      setRetryLastName('');
      setRetryDob('');
    } catch (error: any) {
      showError(error?.message || 'Failed to retry setup');
    }
  };

  const needsReconnect = Boolean(sub.connection_id) && sub.connection_is_active === false;
  const phoneLabel = sub.phone_number || 'No number yet';
  const diceEmailLabel = sub.connection_email || sub.dice_email || 'Not created yet';
  const renewalLabel = sub.cancel_at_period_end ? 'Access until' : 'Renews';

  const chevron = (
    <ChevronDownIcon
      aria-hidden="true"
      className={cx(
        'h-4 w-4 flex-shrink-0 text-white/30 transition-transform',
        expanded && 'rotate-180',
      )}
    />
  );

  /* Collapsed-row cells ------------------------------------------- */

  const phoneCell = (
    <span
      title={phoneLabel}
      className={cx(
        'block truncate font-mono text-sm',
        sub.phone_number ? 'text-white' : 'text-white/40',
      )}
    >
      {phoneLabel}
    </span>
  );

  const emailCell = (
    <span
      title={diceEmailLabel}
      className={cx('block truncate', sub.connection_email || sub.dice_email ? 'text-white/70' : 'text-white/35')}
    >
      {diceEmailLabel}
    </span>
  );

  const statusCell = (
    <div className="flex flex-wrap items-center gap-1.5">
      <StatusBadge status={sub.status} cancelAtPeriodEnd={sub.cancel_at_period_end} />
      {/* IN_USE also reads "Active" — showing it twice on one row is noise, so
          the phone badge appears here only when it says something different.
          Both badges are always rendered in full in the expansion below. */}
      {sub.phone_status && sub.phone_status !== 'IN_USE' && (
        <PhoneStatusBadge status={sub.phone_status} />
      )}
      {needsReconnect && <Badge tone="red">Needs reconnect</Badge>}
      {!sub.forwarding_email && <ForwardingStatusBadge status="NO_EMAIL" />}
    </div>
  );

  const renewalCell = sub.current_period_end ? (
    <span className="block truncate text-xs">
      <span className="text-white/35">{renewalLabel}</span>{' '}
      <span className="text-white/70">{new Date(sub.current_period_end).toLocaleDateString()}</span>
    </span>
  ) : (
    <span className="text-white/25">—</span>
  );

  const stacked = (
    <div className="flex items-start gap-3">
      <div className="min-w-0 flex-1 space-y-1.5">
        <p className={cx('truncate font-mono text-sm', sub.phone_number ? 'text-white' : 'text-white/40')}>
          {phoneLabel}
        </p>
        <p className="truncate text-xs text-white/50" title={diceEmailLabel}>
          {diceEmailLabel}
        </p>
        {statusCell}
        {sub.current_period_end && (
          <p className="text-xs text-white/35">
            {renewalLabel} {new Date(sub.current_period_end).toLocaleDateString()}
          </p>
        )}
      </div>
      {chevron}
    </div>
  );

  /* Expansion ------------------------------------------------------ */

  const detail = (
    <div className="space-y-3">
      {/* Phone + both status badges, exactly as the v1 card header read. */}
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-white">{phoneLabel}</h3>
        <PhoneStatusBadge status={sub.phone_status} />
        <StatusBadge status={sub.status} cancelAtPeriodEnd={sub.cancel_at_period_end} />
      </div>

      {/* What this number does — forwards SMS to the email below */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-white/[0.06] bg-white/[0.03] p-3 text-sm">
        <span className="text-white/40">Texts to this number forward to</span>
        <span className="break-all font-medium text-white">{sub.forwarding_email || 'no email yet'}</span>
        <ForwardingStatusBadge status={sub.forwarding_email ? 'FORWARDED' : 'NO_EMAIL'} />
      </div>

      {/* Details grid */}
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
        <Detail label="Dice account email">{diceEmailLabel}</Detail>
        {(sub.dice_first_name || sub.dice_last_name) && (
          <Detail label="Dice name">
            {[sub.dice_first_name, sub.dice_last_name].filter(Boolean).join(' ')}
          </Detail>
        )}
        {sub.connection_id && sub.connection_is_active === false && (
          <Detail label="Account status">
            <span className="text-red-400">Needs reconnect</span>
          </Detail>
        )}
        {sub.current_period_end && (
          <Detail label={renewalLabel}>
            {new Date(sub.current_period_end).toLocaleDateString()}
          </Detail>
        )}
        <Detail label="Member since">{new Date(sub.created_at).toLocaleDateString()}</Detail>
      </div>
    </div>
  );

  const actions = editingEmail ? (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
      <div className="flex-1">
        <Input
          type="email"
          label="New Forwarding Email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="newemail@gmail.com"
        />
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={handleUpdateEmail}
          disabled={!newEmail || updateEmailMutation.isPending}
        >
          {updateEmailMutation.isPending ? 'Saving...' : 'Save'}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setEditingEmail(false)}>
          Cancel
        </Button>
      </div>
    </div>
  ) : (
    <div className="flex flex-col gap-2">
      {needsSetup && (
        <div className="flex flex-wrap items-center gap-2">
          {showRetryEmail ? (
            <div className="w-full space-y-2">
              <input
                type="email"
                value={retryDiceEmail}
                onChange={e => setRetryDiceEmail(e.target.value)}
                placeholder="Dice email (optional)"
                className={retryInputClass}
              />
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input
                  type="text"
                  value={retryFirstName}
                  onChange={e => setRetryFirstName(e.target.value)}
                  maxLength={100}
                  placeholder="First name (optional)"
                  className={retryInputClass}
                />
                <input
                  type="text"
                  value={retryLastName}
                  onChange={e => setRetryLastName(e.target.value)}
                  maxLength={100}
                  placeholder="Last name (optional)"
                  className={retryInputClass}
                />
              </div>
              <input
                type="date"
                value={retryDob}
                onChange={e => setRetryDob(e.target.value)}
                min={minDob}
                max={maxDob}
                className={retryInputClass}
              />
              <p className="text-[10px] text-white/30">Blank fields fall back to random.</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleRetrySetup}
                  disabled={retryMutation.isPending}
                >
                  {retryMutation.isPending ? 'Retrying...' : 'Retry'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setShowRetryEmail(false); setRetryDiceEmail(''); setRetryFirstName(''); setRetryLastName(''); setRetryDob(''); }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              size="sm"
              onClick={() => { setShowRetryEmail(true); setRetryDiceEmail(sub.connection_email || ''); }}
            >
              Retry Setup
            </Button>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 [&>*]:w-full sm:flex sm:flex-wrap sm:[&>*]:w-auto">

      <Button
        size="sm"
        variant="secondary"
        onClick={() => { setEditingEmail(true); setNewEmail(sub.forwarding_email); }}
      >
        Change Email
      </Button>

      {sub.status === 'active' && !sub.cancel_at_period_end && (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setConfirmCancel(true)}
          disabled={cancelMutation.isPending}
        >
          {cancelMutation.isPending ? 'Canceling...' : 'Cancel'}
        </Button>
      )}

      {sub.cancel_at_period_end && (
        <Button
          size="sm"
          variant="primary"
          onClick={handleResubscribe}
          disabled={resubMutation.isPending}
        >
          {resubMutation.isPending ? 'Reactivating...' : 'Reactivate'}
        </Button>
      )}

      {sub.status === 'canceled' && (
        <Link to={v2Path('/dice/signup')} className={linkButtonClass()}>
          Resubscribe
        </Link>
      )}

      {sub.status === 'pending' && (
        <>
          <Button
            size="sm"
            variant="primary"
            onClick={handleResumePending}
            disabled={resumeCheckoutMutation.isPending}
          >
            {resumeCheckoutMutation.isPending ? 'Loading...' : 'Resume Payment'}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={handleCancelPending}
            disabled={cancelPendingMutation.isPending}
          >
            {cancelPendingMutation.isPending ? 'Canceling...' : 'Cancel Pending Order'}
          </Button>
        </>
      )}
      </div>
    </div>
  );

  return (
    <DataListRow
      cells={[phoneCell, emailCell, statusCell, renewalCell]}
      stacked={stacked}
      onClick={onToggle}
      expanded={expanded}
      trailing={chevron}
    >
      {expanded && (
        <div
          className={cx(
            'mt-2 space-y-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4',
            'lg:mt-0 lg:rounded-none lg:border-0 lg:border-b lg:border-white/[0.05] lg:bg-white/[0.015] lg:px-4 lg:py-4',
          )}
        >
          {detail}
          {actions}
        </div>
      )}

      {/* Outside the disclosure: collapsing a row must not strand an open dialog. */}
      <ConfirmDialog
        open={confirmCancel}
        title="Cancel this Dice number?"
        message={
          <>
            You'll keep access and SMS forwarding until{' '}
            <span className="text-white">
              {sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : 'the end of your billing period'}
            </span>
            . You can reactivate any time before then.
          </>
        }
        confirmLabel="Cancel subscription"
        cancelLabel="Keep it"
        destructive
        busy={cancelMutation.isPending}
        onConfirm={handleCancel}
        onClose={() => setConfirmCancel(false)}
      />
    </DataListRow>
  );
}

const SMS_COLUMNS: DataListColumn[] = [
  { key: 'route', label: 'From / To', span: 3 },
  { key: 'status', label: 'Forwarding', span: 2 },
  { key: 'body', label: 'Message', span: 5 },
  { key: 'received', label: 'Received', span: 2 },
];

const EMPTY_SUBTITLE = 'Manage your Dice subscriptions and phone numbers';

/** Below this the toolbar is pure overhead, so the list renders bare. */
const TOOLBAR_MIN_ACCOUNTS = 2;

const BULK_MAX = 50;

function BulkJobStatusBadge({ status }: { status: string }) {
  const map: Record<string, { tone: BadgeTone; label: string }> = {
    pending: { tone: 'neutral', label: 'Queued' },
    processing: { tone: 'blue', label: 'Creating...' },
    completed: { tone: 'green', label: 'Completed' },
    failed: { tone: 'red', label: 'Failed' },
    cancelled: { tone: 'neutral', label: 'Cancelled' },
  };
  const entry = map[status] || { tone: 'neutral' as BadgeTone, label: status };
  return <Badge tone={entry.tone}>{entry.label}</Badge>;
}

/**
 * Bulk account creation: pick a quantity, accounts are created in the
 * background from the platform number pool, billed as quantity on the
 * consolidated subscription. Polls the job while it runs.
 */
function BulkCreatePanel() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [quantity, setQuantity] = useState('5');
  const [jobId, setJobId] = useState<number | null>(null);

  const createMutation = useDiceBulkCreate();
  const cancelMutation = useDiceBulkCancel();
  const { data: jobsData } = useDiceBulkJobs();
  const { data: job } = useDiceBulkJob(jobId);

  // Resume watching a still-running job after a reload.
  useEffect(() => {
    if (jobId != null) return;
    const running = jobsData?.jobs?.find((j) => j.status === 'pending' || j.status === 'processing');
    if (running) setJobId(running.id);
  }, [jobsData, jobId]);

  // When the watched job finishes, refresh the account list.
  const prevStatus = useRef<string | null>(null);
  useEffect(() => {
    const s = job?.status ?? null;
    const wasRunning = prevStatus.current === 'pending' || prevStatus.current === 'processing';
    if (wasRunning && s && s !== 'pending' && s !== 'processing') {
      queryClient.invalidateQueries({ queryKey: queryKeys.diceSubscription });
      queryClient.invalidateQueries({ queryKey: queryKeys.diceBulkJobs });
      if (s === 'completed') {
        showSuccess(`Bulk creation finished — ${job?.created_count ?? 0} account(s) created.`);
      }
    }
    prevStatus.current = s;
  }, [job?.status, job?.created_count, queryClient, showSuccess]);

  const isRunning = job?.status === 'pending' || job?.status === 'processing';

  const handleStart = async () => {
    const qty = parseInt(quantity, 10);
    if (!Number.isFinite(qty) || qty < 1 || qty > BULK_MAX) {
      showError(`Enter a quantity between 1 and ${BULK_MAX}.`);
      return;
    }
    try {
      const created = await createMutation.mutateAsync({ quantity: qty });
      trackEvent('dice_bulk_create_started', { quantity: qty });
      setJobId(created.id);
      showSuccess(`Creating ${qty} Dice account(s) in the background...`);
    } catch (error: any) {
      showError(error?.message || 'Failed to start bulk creation');
    }
  };

  const handleCancel = async () => {
    if (!job) return;
    try {
      await cancelMutation.mutateAsync(job.id);
      showSuccess('Bulk creation cancelled — accounts already created stay.');
    } catch (error: any) {
      showError(error?.message || 'Failed to cancel');
    }
  };

  const doneCount = (job?.created_count ?? 0) + (job?.failed_count ?? 0);
  const pct = job && job.quantity > 0 ? Math.round((doneCount / job.quantity) * 100) : 0;

  return (
    <div className={cx(CARD_SURFACE, 'p-4')}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-white">Bulk create accounts</div>
          <div className="text-xs text-white/40">
            Numbers come from the pool; each account bills $1/mo on your subscription.
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <input
            type="number"
            min={1}
            max={BULK_MAX}
            aria-label="Number of accounts to create"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            disabled={isRunning}
            className={cx(
              'w-20 rounded-xl border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-sm text-white',
              'transition-colors focus:border-white/20 focus:outline-none',
              FOCUS_RING,
            )}
          />
          <Button size="sm" onClick={handleStart} disabled={isRunning || createMutation.isPending}>
            {createMutation.isPending ? 'Starting...' : isRunning ? 'Running...' : 'Create'}
          </Button>
        </div>
      </div>

      {job && (
        <div className="mt-4 border-t border-white/[0.06] pt-4">
          <div className="flex flex-wrap items-center gap-3 text-xs text-white/60">
            <BulkJobStatusBadge status={job.status} />
            <span>
              {job.created_count} of {job.quantity} created
              {job.failed_count > 0 ? ` · ${job.failed_count} failed` : ''}
            </span>
            {isRunning && (
              <button
                type="button"
                onClick={handleCancel}
                disabled={cancelMutation.isPending}
                className={cx('ml-auto rounded-md px-1 py-1 text-xs text-white/40 hover:text-white/60', FOCUS_RING)}
              >
                {cancelMutation.isPending ? 'Cancelling...' : 'Cancel'}
              </button>
            )}
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
            <div
              className={cx(
                'h-full rounded-full transition-all',
                job.status === 'failed' ? 'bg-red-400/70' : 'bg-emerald-400/70',
              )}
              style={{ width: `${pct}%` }}
            />
          </div>
          {job.last_error && (
            <div className="mt-2 text-xs text-red-300/80">{job.last_error}</div>
          )}
        </div>
      )}
    </div>
  );
}

export const DiceSettingsScreen: React.FC = () => {
  const { showError } = useToast();
  const { data: subData, isLoading } = useDiceSubscriptions();
  const { data: smsData } = useDiceMySMS(20);
  const billingMutation = useDiceBillingPortal();

  useEffect(() => { trackEvent('dice_settings_page_viewed'); }, []);

  const subscriptions = useMemo(() => subData?.subscriptions || [], [subData?.subscriptions]);
  const smsLogs = smsData?.logs || [];

  const [searchQuery, setSearchQuery] = useState('');
  const [{ sort: sortMode, filter: statusFilter }, setViewPref] = useState<ViewPref>(readViewPref);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set<number>());

  const setSortMode = (sort: SortMode) => setViewPref((prev) => ({ ...prev, sort }));
  const setStatusFilter = (filter: StatusFilter) => setViewPref((prev) => ({ ...prev, filter }));

  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_PREF_KEY, JSON.stringify({ sort: sortMode, filter: statusFilter }));
    } catch {
      // Blocked storage — the view still works, it just won't be remembered.
    }
  }, [sortMode, statusFilter]);

  // A single account has nothing to scan, so open it the way the old card was.
  const autoExpanded = useRef(false);
  useEffect(() => {
    if (autoExpanded.current || subscriptions.length === 0) return;
    autoExpanded.current = true;
    if (subscriptions.length === 1) setExpandedIds(new Set([subscriptions[0].id]));
  }, [subscriptions]);

  const toggleRow = (id: number) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const hasSubscriptions = subscriptions.length > 0;
  const showToolbar = subscriptions.length >= TOOLBAR_MIN_ACCOUNTS;

  const flagsById = useMemo(() => {
    const map = new Map<number, Flags>();
    subscriptions.forEach((sub) => map.set(sub.id, classify(sub)));
    return map;
  }, [subscriptions]);

  const counts = useMemo(() => {
    const acc = { all: subscriptions.length, attention: 0, active: 0, canceled: 0 };
    subscriptions.forEach((sub) => {
      const bucket = flagsById.get(sub.id)!.bucket;
      if (bucket === 'attention') acc.attention += 1;
      else if (bucket === 'inactive') acc.canceled += 1;
      else acc.active += 1;
    });
    return acc;
  }, [subscriptions, flagsById]);

  const visible = useMemo(() => {
    if (!showToolbar) return subscriptions;
    const query = searchQuery.trim().toLowerCase();
    const queryDigits = digitsOnly(query);
    const filtered = subscriptions.filter((sub) => {
      const bucket = flagsById.get(sub.id)!.bucket;
      if (statusFilter === 'attention' && bucket !== 'attention') return false;
      if (statusFilter === 'active' && bucket !== 'active' && bucket !== 'ending') return false;
      if (statusFilter === 'canceled' && bucket !== 'inactive') return false;
      return matchesQuery(sub, query, queryDigits);
    });

    const sorted = [...filtered];
    if (sortMode === 'phone') {
      sorted.sort(byPhoneNumber);
    } else if (sortMode === 'newest') {
      sorted.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    } else {
      sorted.sort((a, b) => {
        const fa = flagsById.get(a.id)!;
        const fb = flagsById.get(b.id)!;
        const bucketDelta = BUCKET_ORDER.indexOf(fa.bucket) - BUCKET_ORDER.indexOf(fb.bucket);
        if (bucketDelta !== 0) return bucketDelta;
        if (fa.severity !== fb.severity) return fa.severity - fb.severity;
        return byPhoneNumber(a, b);
      });
    }
    return sorted;
  }, [subscriptions, showToolbar, searchQuery, statusFilter, sortMode, flagsById]);

  /** Status-sorted lists get headings; the other sorts are flat by definition. */
  const groups = useMemo(() => {
    if (sortMode !== 'status') return null;
    const out: { bucket: Bucket; rows: DiceSubscription[] }[] = [];
    visible.forEach((sub) => {
      const bucket = flagsById.get(sub.id)!.bucket;
      const last = out[out.length - 1];
      if (last && last.bucket === bucket) last.rows.push(sub);
      else out.push({ bucket, rows: [sub] });
    });
    return out.length > 1 ? out : null;
  }, [visible, sortMode, flagsById]);

  const filterOptions: SegmentOption<StatusFilter>[] = [
    { value: 'all', label: <>All <span className="text-white/30">{counts.all}</span></> },
    {
      value: 'attention',
      label: <>{BUCKET_LABEL.attention} <span className="text-white/30">{counts.attention}</span></>,
      title: 'Past due, needs reconnect, setting up, pending, or missing a forwarding email',
    },
    { value: 'active', label: <>Active <span className="text-white/30">{counts.active}</span></> },
    { value: 'canceled', label: <>Canceled <span className="text-white/30">{counts.canceled}</span></> },
  ];

  const isFiltered = showToolbar && (searchQuery.trim() !== '' || statusFilter !== 'all');

  const handleBillingPortal = async () => {
    try {
      const data = await billingMutation.mutateAsync(window.location.href);
      trackEvent('dice_billing_portal_opened');
      window.location.href = data.portal_url;
    } catch (error: any) {
      showError(error?.message || 'Failed to open billing portal');
    }
  };

  if (isLoading) {
    return (
      <PageShell>
        <PageHeader title="Dice Accounts" subtitle={EMPTY_SUBTITLE} />
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader
        title="Dice Accounts"
        subtitle={
          hasSubscriptions
            ? `${subscriptions.length} subscription${subscriptions.length !== 1 ? 's' : ''} — each number is its own Dice account`
            : EMPTY_SUBTITLE
        }
        actions={
          hasSubscriptions ? (
            <>
              <Link to={v2Path('/dice/signup')} className={linkButtonClass()} data-tour="dice-create">
                Add Account
              </Link>
              <Button
                size="sm"
                variant="secondary"
                onClick={handleBillingPortal}
                disabled={billingMutation.isPending}
              >
                {billingMutation.isPending ? 'Opening...' : 'Manage Billing'}
              </Button>
            </>
          ) : undefined
        }
      />

      <div className="space-y-6">
        <BulkCreatePanel />

        {/* Toolbar — search first, never inside a disclosure (DESIGN_PLAN §4.4). */}
        {showToolbar && (
          <div className={cx(CARD_SURFACE, 'p-4')}>
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
              <input
                type="text"
                aria-label="Search Dice accounts"
                placeholder="Search by phone number or Dice email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={cx(
                  'w-full rounded-xl border border-white/[0.08] bg-white/[0.03] py-3 pl-11 pr-11 text-sm text-white',
                  'placeholder-white/30 transition-colors focus:border-white/20 focus:outline-none',
                  FOCUS_RING,
                )}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                  title="Clear search"
                  className={cx(
                    'absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-white/30',
                    'transition-colors hover:text-white/60',
                    FOCUS_RING,
                  )}
                >
                  <CloseIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-white/[0.06] pt-4">
              <SegmentedControl
                label="Filter accounts by status"
                size="sm"
                options={filterOptions}
                value={statusFilter}
                onChange={setStatusFilter}
              />
              {/* A dropdown, not a second SegmentedControl. Side by side the two
                  read as one set of tabs, but one hides rows and the other only
                  reorders them — so the sort is made visually subordinate
                  instead of competing with the filter. */}
              <label className="flex items-center gap-2">
                <span className="text-xs text-white/40">Sort</span>
                <select
                  value={sortMode}
                  onChange={(e) => setSortMode(e.target.value as SortMode)}
                  aria-label="Sort accounts"
                  className={cx(
                    'rounded-lg border border-white/[0.10] bg-white/[0.04] px-2.5 py-1.5 text-xs font-medium text-white/80',
                    'hover:border-white/20 focus:outline-none',
                    FOCUS_RING,
                  )}
                >
                  {SORT_MODES.map((mode) => (
                    <option key={mode} value={mode}>{SORT_LABEL[mode]}</option>
                  ))}
                </select>
              </label>
              <div className="ml-auto flex items-center gap-3">
                <span className="text-xs text-white/40">
                  Showing {visible.length} of {subscriptions.length}
                </span>
                {isFiltered && (
                  <button
                    type="button"
                    onClick={() => { setSearchQuery(''); setStatusFilter('all'); }}
                    className={cx(
                      'rounded-md px-1 py-1 text-xs text-white/40 transition-colors hover:text-white/60',
                      FOCUS_RING,
                    )}
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* One dense row per Dice account/number. */}
        {hasSubscriptions ? (
          visible.length > 0 ? (
            <DataList columns={SUB_COLUMNS} trailing>
              {groups
                ? groups.map((group) => (
                    <React.Fragment key={group.bucket}>
                      <div
                        className={cx(
                          'flex items-center gap-2 pt-2 lg:border-b lg:border-white/[0.05]',
                          'lg:bg-white/[0.015] lg:px-4 lg:py-2 lg:pt-2',
                        )}
                      >
                        <span
                          className={cx(
                            'text-[11px] font-medium uppercase tracking-wider',
                            group.bucket === 'attention' ? 'text-amber-400/80' : 'text-white/35',
                          )}
                        >
                          {BUCKET_LABEL[group.bucket]}
                        </span>
                        <span className="text-[11px] text-white/25">{group.rows.length}</span>
                      </div>
                      {group.rows.map((sub) => (
                        <SubscriptionRow
                          key={sub.id}
                          sub={sub}
                          expanded={expandedIds.has(sub.id)}
                          onToggle={() => toggleRow(sub.id)}
                        />
                      ))}
                    </React.Fragment>
                  ))
                : visible.map((sub) => (
                    <SubscriptionRow
                      key={sub.id}
                      sub={sub}
                      expanded={expandedIds.has(sub.id)}
                      onToggle={() => toggleRow(sub.id)}
                    />
                  ))}
            </DataList>
          ) : (
            <EmptyState
              title="No accounts match your search."
              body="Try a different number or email, or clear the status filter."
              action={
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => { setSearchQuery(''); setStatusFilter('all'); }}
                >
                  Clear all
                </Button>
              }
            />
          )
        ) : (
          <EmptyState
            title="No Dice subscriptions yet."
            action={
              <Link to={v2Path('/dice/signup')} className={linkButtonClass()} data-tour="dice-create">
                Create Dice Account
              </Link>
            }
          />
        )}

        {/*
          SMS Logs. v1 renders this only inside the has-subscriptions branch
          (the empty branch returns early), so the subscription gate is kept
          here rather than widened to every user with logs — that would be a
          new surface, not a re-layout.
        */}
        {hasSubscriptions && smsLogs.length > 0 && (
          <Panel title="Recent SMS Messages" padding="none" tourId="dice-sms">
            <DataList columns={SMS_COLUMNS} className="lg:rounded-none lg:border-0 lg:bg-transparent">
              {smsLogs.map((log) => {
                const preview = log.body.length > 120 ? `${log.body.substring(0, 120)}...` : log.body;
                return (
                  <DataListRow
                    key={log.id}
                    cells={[
                      <span className="block truncate text-xs text-white/40" title={`From ${log.from_number} to ${log.to_number}`}>
                        From {log.from_number} to {log.to_number}
                      </span>,
                      <ForwardingStatusBadge status={log.forwarding_status} />,
                      <span className="block truncate" title={log.body}>{preview}</span>,
                      <span className="block truncate text-xs text-white/40">
                        {new Date(log.received_at).toLocaleString()}
                      </span>,
                    ]}
                    stacked={
                      <div className="space-y-1.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs text-white/40">
                            From {log.from_number} to {log.to_number}
                          </span>
                          <ForwardingStatusBadge status={log.forwarding_status} />
                        </div>
                        <p className="break-words text-sm text-white/70">{preview}</p>
                        <p className="text-xs text-white/40">
                          {new Date(log.received_at).toLocaleString()}
                        </p>
                      </div>
                    }
                  />
                );
              })}
            </DataList>
          </Panel>
        )}

        {/*
          How it works — onboarding copy, so it sits below the list and is
          collapsed by default, expanded only when there is nothing else to read
          (DESIGN_PLAN §3.8).
        */}
        <details
          open={!hasSubscriptions}
          className="group rounded-2xl border border-blue-500/15 bg-blue-500/[0.06]"
        >
          <summary
            className={cx(
              'flex cursor-pointer list-none items-center gap-3 p-4 marker:content-none',
              FOCUS_RING,
            )}
          >
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-500/15 text-blue-300">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
            <span className="flex-1 text-sm font-medium text-white">How it works</span>
            <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-white/40 transition-transform group-open:rotate-180" />
          </summary>
          <p className="px-4 pb-4 pl-[60px] text-sm leading-relaxed text-white/60">
            Each number is a dedicated phone line for its own Dice account. Texts Dice sends to it
            (verification codes, ticket links) are forwarded straight to your email — so you never
            need a second SIM. Manage the email per number below.
          </p>
        </details>
      </div>
    </PageShell>
  );
};
